import * as yup from 'yup';
import { ptBrCommon } from '@/lib/pt-br/messages';
import type { SubmitHandler } from 'react-hook-form';
import type { ForgotPasswordUserInput, ResetPasswordOptionsResponse } from '@/types';
import { Form } from '@/components/ui/forms/form';
import Input from '@/components/ui/forms/input';
import Button from '@/components/ui/button';
import { createStore, GlobalState } from 'little-state-machine';
import { useModalAction } from '@/components/ui/modal/modal.context';
import Logo from '@/components/ui/logo';
import { useState } from 'react';
import { useMutation } from 'react-query';
import client from '@/framework/client';

// ========== TEMPORARY: Original simple email-only flow (attack mitigation) ==========

const emailFormValidation = yup.object().shape({
  email: yup
    .string()
    .email('error-email-format')
    .required('error-email-required'),
});

function EmailForm({
  email,
  onSubmit,
  isLoading,
  serverError,
}: Readonly<{
  email: string;
  onSubmit: SubmitHandler<Pick<ForgotPasswordUserInput, 'email'>>;
  isLoading: boolean;
  serverError: string | null;
}>) {
return (
    <Form<Pick<ForgotPasswordUserInput, 'email'>>
      onSubmit={onSubmit}
      useFormProps={{
        defaultValues: { email },
      }}
      validationSchema={emailFormValidation}
      serverError={serverError ? { email: serverError } : undefined}
      className="text-left"
    >
      {({ register, formState: { errors } }) => (
        <>
          <p className="mt-4 mb-7 text-center text-sm leading-relaxed text-body sm:mt-5 sm:mb-10 md:text-base">
            {"Enviaremos um link para o endereço digitado"}
          </p>
          <Input
            label={"E-mail"}
            type="email"
            {...register('email')}
            error={ptBrCommon(errors.email?.message ?? '')}
          />
          <p className="lg:ml-2 text-[12px] mt-2 text-gray-500 leading-relaxed">
            Não se esqueça de olhar a caixa de spam ou lixo eletrônico.
          </p>
          <Button
            type="submit"
            className="!mt-5 w-full text-sm tracking-[0.2px] lg:!mt-6"
            loading={isLoading}
            disabled={isLoading}
          >
            Enviar
          </Button>
        </>
      )}
    </Form>
  );
}

function SimpleConfirmationView() {
  return (
    <div className="text-center mt-4">
      <div className="mb-6 rounded-lg p-6">
        <svg
          className="mx-auto h-16 w-16 text-color-primary mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
        <p className="text-sm text-gray-700 leading-relaxed mb-3">
          Se o e-mail informado estiver cadastrado, você receberá um link para
          redefinir sua senha em instantes.
        </p>
        <p className="text-sm text-gray-600 leading-relaxed">
          Verifique sua caixa de entrada e também a pasta de spam.
        </p>
      </div>
    </div>
  );
}

function SimpleForgotPasswordForm() {
  const { openModal } = useModalAction();
  const [step, setStep] = useState<'Email' | 'ConfirmedSent'>('Email');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const { mutate: forgotPasswordWithEmail, isLoading } = useMutation(
    client.users.forgotPasswordWithEmail,
    {
      onSuccess: () => {
        setFormError(null);
        setStep('ConfirmedSent');
      },
      onError: (err: any) => {
        const message =
          err?.response?.data?.message ??
          'Não foi possível enviar. Tente novamente.';
        setFormError(message);
      },
    },
  );

  const emailFormHandle: SubmitHandler<
    Pick<ForgotPasswordUserInput, 'email'>
  > = ({ email: submittedEmail }) => {
    setEmail(submittedEmail);
    setFormError(null);
    forgotPasswordWithEmail({ email: submittedEmail });
  };

  return (
    <div>
      {step === 'Email' && (
        <EmailForm
          email={email}
          onSubmit={emailFormHandle}
          isLoading={isLoading}
          serverError={formError}
        />
      )}
      {step === 'ConfirmedSent' && <SimpleConfirmationView />}
      <Button
        type="button"
        className="!mt-5 w-full text-sm tracking-[0.2px] !border !border-color-primary !text-color-primary bg-white hover:bg-color-primary hover:!text-white"
        onClick={() => openModal('LOGIN_VIEW')}
      >
        Voltar
      </Button>
    </div>
  );
}

// ========== Exports for state machine (used by user.ts and future CPF flow) ==========

export type ForgotStep = 'Cpf' | 'ContactOptions' | 'ConfirmedSent' | 'Password';

export interface ForgotPasswordState {
  step: ForgotStep;
  cpf: string;
  email: string;
  password: string;
  token: string;
  contactOptions: ResetPasswordOptionsResponse | null;
  selectedContactType: 'email' | 'cellphone' | 'phone' | null;
}

export const initialState: ForgotPasswordState = {
  step: 'Cpf',
  cpf: '',
  email: '',
  password: '',
  token: '',
  contactOptions: null,
  selectedContactType: null,
};

//@ts-ignore
createStore(initialState as GlobalState);

export const updateFormState = (
  state: GlobalState,
  payload: Partial<ForgotPasswordState> & { [key: string]: any },
): GlobalState => ({ ...state, ...payload } as GlobalState);

// ========== Default: simple email-only flow ==========

export default function ForgotUserPassword() {
  return (
    <div className="flex h-full min-h-screen w-screen flex-col justify-center bg-light py-6 px-5 sm:p-8 md:h-auto md:min-h-0 md:max-w-[480px] md:rounded-xl">
      <div className="flex justify-center">
        <Logo />
      </div>
      <SimpleForgotPasswordForm />
    </div>
  );
}

/* ========== COMMENTED OUT: CPF + contact options flow (restore when needed) ==========

const cpfFormValidation = yup.object().shape({
  cpf: yup
    .string()
    .required('Informe seu CPF')
    .test('cpf', 'CPF inválido', (value = '') => cpfValidator(value)),
});

const passwordFormValidation = yup.object().shape({
  password: yup.string().required(),
});

function CpfForm({
  cpf,
  onSubmit,
  isLoading,
  serverError,
}: {
  cpf: string;
  onSubmit: SubmitHandler<{ cpf: string }>;
  isLoading: boolean;
  serverError: string | null;
}) {
return (
    <Form<{ cpf: string }>
      onSubmit={onSubmit}
      useFormProps={{
        defaultValues: { cpf },
      }}
      validationSchema={cpfFormValidation}
      serverError={serverError ? { cpf: serverError } : undefined}
      className="text-left"
    >
      {({ register, formState: { errors }, setValue }) => (
        <>
          <p className="mt-4 mb-7 text-center text-sm leading-relaxed text-body sm:mt-5 sm:mb-10 md:text-base">
            Informe seu CPF para encontrarmos as opções de contato cadastradas e
            enviarmos o link para redefinir sua senha.
          </p>
          <Input
            label="CPF"
            type="text"
            placeholder="000.000.000-00"
            maxLength={14}
            {...register('cpf', {
              onChange: (e) => setValue('cpf', cpfMask(e.target.value), { shouldValidate: true }),
            })}
            error={errors.cpf?.message}
          />
          <Button
            type="submit"
            className="!mt-5 w-full text-sm tracking-[0.2px] lg:!mt-6"
            loading={isLoading}
            disabled={isLoading}
          >
            Continuar
          </Button>
        </>
      )}
    </Form>
  );
}

function ContactOptionCard({
  option,
  selected,
  onSelect,
}: {
  option: ResetPasswordContactOption;
  selected: boolean;
  onSelect: () => void;
}) {
  const isEmail = option.type === 'email';
  const label =
    option.type === 'email'
      ? 'E-mail'
      : option.type === 'cellphone'
        ? 'Celular'
        : 'Telefone';
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`w-full text-left rounded-xl border-2 p-4 transition-all flex items-center gap-3 ${selected
        ? 'border-color-primary bg-color-primary/5'
        : 'border-gray-200 hover:border-gray-900 bg-white'
        }`}
    >
      <span
        className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${isEmail ? 'bg-amber-100 text-amber-700' : 'bg-sky-100 text-sky-700'
          }`}
      >
        {isEmail ? (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        ) : (
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
        )}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-body">{label}</p>
        <p className="text-sm text-gray-600 truncate" title={option.masked}>
          {option.masked}
        </p>
      </div>
      {selected && (
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-color-primary text-white">
          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </span>
      )}
    </button>
  );
}

function ContactOptionsView({
  contactOptions,
  onSelectAndSend,
  isLoading,
  serverError,
}: {
  contactOptions: ResetPasswordOptionsResponse;
  onSelectAndSend: (contactType: 'email' | 'cellphone' | 'phone') => void;
  isLoading: boolean;
  serverError: string | null;
}) {
  const emailOptions: ResetPasswordContactOption[] = contactOptions.emails ?? [];
  const cellphoneOptions: ResetPasswordContactOption[] =
    (contactOptions.phones ?? []).filter((option) => option.type === 'cellphone');
  const isDevelopment =
    process.env.NEXT_PUBLIC_APPLICATION_MODE === 'development';
  const allOptions: ResetPasswordContactOption[] = isDevelopment
    ? [...emailOptions, ...cellphoneOptions]
    : [...emailOptions];

  const [selectedIndex, setSelectedIndex] = useState<number | null>(
    allOptions.length > 0 ? 0 : null,
  );
  const selectedOption =
    selectedIndex != null ? allOptions[selectedIndex] ?? null : null;

  return (
    <div className="text-left">
      <p className="mt-4 mb-4 text-center text-sm leading-relaxed text-body">
        Escolha por onde deseja receber o link para redefinir sua senha:
      </p>
      <div className="space-y-3 mb-6">
        {allOptions.map((option, index) => (
          <ContactOptionCard
            key={`${index}-${option.type}-${option.masked}`}
            option={option}
            selected={selectedIndex === index}
            onSelect={() => setSelectedIndex(index)}
          />
        ))}
      </div>
      {serverError && (
        <p className="text-sm text-red-600 mb-3 text-center">{serverError}</p>
      )}
      <Button
        type="button"
        className="w-full text-sm tracking-[0.2px] !mt-2"
        disabled={selectedOption == null || isLoading}
        loading={isLoading}
        onClick={() => selectedOption && onSelectAndSend(selectedOption.type)}
      >
        Enviar link
      </Button>
    </div>
  );
}

function ConfirmationView({
  contactType,
}: {
  contactType: 'email' | 'cellphone' | 'phone' | null;
}) {
  const isEmail = contactType === 'email';

  return (
    <div className="text-center mt-4">
      <div className="mb-6 rounded-lg p-6">
        {isEmail ? (
          <svg
            className="mx-auto h-16 w-16 text-color-primary mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        ) : (
          <svg
            className="mx-auto h-16 w-16 text-color-primary mb-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
            />
          </svg>
        )}
        {isEmail ? (
          <>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              Enviamos um link para redefinir sua senha no e-mail cadastrado.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Verifique sua caixa de entrada e também a pasta de spam ou lixo
              eletrônico.
            </p>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-700 leading-relaxed mb-3">
              Enviamos um SMS com o link para redefinir sua senha no número
              cadastrado.
            </p>
            <p className="text-sm text-gray-600 leading-relaxed">
              Verifique as mensagens do seu celular e toque no link para criar
              uma nova senha.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function PasswordForm({
  onSubmit,
  isLoading,
  handlePrevStep,
}: {
  onSubmit: SubmitHandler<Pick<ResetPasswordUserInput, 'password'>>;
  isLoading: boolean;
  handlePrevStep: () => void;
}) {
return (
    <Form<Pick<ResetPasswordUserInput, 'password'>>
      onSubmit={onSubmit}
      useFormProps={{
        defaultValues: { password: '' },
      }}
      validationSchema={passwordFormValidation}
    >
      {({ register, formState: { errors } }) => (
        <>
          <PasswordInput
            label={"Nova Senha"}
            {...register('password')}
            error={ptBrCommon(errors.password?.message!)}
          />
          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Button
              onClick={handlePrevStep}
              className="order-1 w-full !bg-cyan-500 text-sm tracking-[0.2px] hover:!bg-cyan-600"
            >
              <ArrowPrevIcon className="w-5" />
              {"Passo Anterior"}
            </Button>
            <Button
              type="submit"
              className="w-full text-sm tracking-[0.2px] sm:order-2"
              loading={isLoading}
              disabled={isLoading}
            >
              {"Redefinir Senha"}
            </Button>
          </div>
        </>
      )}
    </Form>
  );
}

function RenderFormSteps() {
  const { openModal } = useModalAction();
  const { mutate: getOptions, isLoading: loadingOptions, formError: optionsError } =
    useGetResetPasswordOptions();
  const { mutate: sendReset, isLoading: sendingReset, formError: sendError } =
    useForgotPassword();
  const { mutate: resetPassword, isLoading: resetting } = useResetPassword();
  const { state: rawState, actions } = useStateMachine({ updateFormState });
  const state = rawState as ForgotPasswordState;

  useEffect(() => {
    actions.updateFormState({
      step: 'Cpf' as ForgotStep,
      cpf: '',
      email: '',
      password: '',
      token: '',
      contactOptions: null,
      selectedContactType: null,
    });
  }, [actions]);

  const cpfFormHandle: SubmitHandler<{ cpf: string }> = ({ cpf }) => {
    getOptions({ cpf: removeCpfMask(cpf) });
  };

  const handleSelectAndSend = (contactType: 'email' | 'cellphone' | 'phone') => {
    const cpfDigits = state.cpf ? removeCpfMask(state.cpf) : '';
    if (!cpfDigits) return;
    clientLogger.info('Forgot password: reset link requested', {
      cpf: cpfDigits,
      contactType,
      screen: 'forgot-password',
    });
    sendReset({ cpf: cpfDigits, contactType });
  };

  const passwordFormHandle: SubmitHandler<
    Pick<ResetPasswordUserInput, 'password'>
  > = ({ password }) => {
    resetPassword({ password, token: state.token || '' });
  };

  const backToContactOptions = () => {
    actions.updateFormState({ step: 'ContactOptions' });
  };

  const backToCpf = () => {
    actions.updateFormState({
      step: 'Cpf',
      contactOptions: null,
      selectedContactType: null,
    });
  };

  const sendFormError =
    sendError && typeof sendError === 'object' && sendError?.contactType
      ? String(sendError.contactType)
      : null;

  return (
    <div>
      {state.step === 'Cpf' && (
        <CpfForm
          cpf={state.cpf ? cpfMask(state.cpf) : ''}
          onSubmit={cpfFormHandle}
          isLoading={loadingOptions}
          serverError={optionsError}
        />
      )}
      {state.step === 'ContactOptions' && state.contactOptions && (
        <ContactOptionsView
          contactOptions={state.contactOptions}
          onSelectAndSend={handleSelectAndSend}
          isLoading={sendingReset}
          serverError={sendFormError}
        />
      )}
      {state.step === 'ConfirmedSent' && (
        <ConfirmationView contactType={state.selectedContactType} />
      )}
      {state.step === 'Password' && (
        <PasswordForm
          onSubmit={passwordFormHandle}
          isLoading={resetting}
          handlePrevStep={() => actions.updateFormState({ step: 'ContactOptions' })}
        />
      )}
      <Button
        type="button"
        className="!mt-5 w-full text-sm tracking-[0.2px] !border !border-color-primary !text-color-primary bg-white hover:bg-color-primary hover:!text-white"
        onClick={() =>
          state.step === 'ContactOptions'
            ? backToCpf()
            : openModal('LOGIN_VIEW')
        }
      >
        {state.step === 'ContactOptions' ? (
          <>
            <ArrowPrevIcon className="w-5" />
            Voltar
          </>
        ) : (
          'Voltar'
        )}
      </Button>
    </div>
  );
}

To restore the CPF flow, uncomment the block above and change the default export to:

export default function ForgotUserPassword() {
  return (
    <StateMachineProvider>
      <div className="flex h-full min-h-screen w-screen flex-col justify-center bg-light py-6 px-5 sm:p-8 md:h-auto md:min-h-0 md:max-w-[480px] md:rounded-xl">
        <div className="flex justify-center">
          <Logo />
        </div>
        <RenderFormSteps />
      </div>
    </StateMachineProvider>
  );
}

And comment out or remove the SimpleForgotPasswordForm and email-only flow.
========== END COMMENTED OUT ========== */
